import type React from 'react'

/**
 * StationDetails-local SVG glyphs that the shared `@mammoth/design-kit`
 * icon registry does not (yet) ship. Each is the verbatim Figma export
 * (section 6967:29525), re-stroked with `currentColor` so it binds to the
 * surrounding text-colour token (never a literal hex). Cross-platform
 * peers are SF Symbols on iOS (`exclamationmark.bubble`, `wifi.slash`,
 * `magnifyingglass`) and vector drawables on Android — documented
 * affordance §10 (icon source differs, glyph is identical).
 *
 * TODO(@mammoth/design-kit): lift `reportBubble` / `wifiOff` /
 * `magnifier` into the shared icon registry when a second consumer
 * appears, so this module can collapse to `Icon` imports.
 */

interface GlyphProps {
  /** Square edge length in px. */
  size: number
  className?: string
}

/** `exclamationmark.bubble` — Figma node 7051:30287 (18×18). */
export function ReportBubbleGlyph({ size, className }: GlyphProps): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M3 2H15C15.5304 2 16.0391 2.21071 16.4142 2.58579C16.7893 2.96086 17 3.46957 17 4V11C17 11.5304 16.7893 12.0391 16.4142 12.4142C16.0391 12.7893 15.5304 13 15 13H8L4 16.5V13H3C2.46957 13 1.96086 12.7893 1.58579 12.4142C1.21071 12.0391 1 11.5304 1 11V4C1 3.46957 1.21071 2.96086 1.58579 2.58579C1.96086 2.21071 2.46957 2 3 2Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path d="M9 5V8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path
        d="M9 11.2C9.33137 11.2 9.6 10.9314 9.6 10.6C9.6 10.2686 9.33137 10 9 10C8.66863 10 8.4 10.2686 8.4 10.6C8.4 10.9314 8.66863 11.2 9 11.2Z"
        fill="currentColor"
      />
    </svg>
  )
}

/** `wifi.slash` — Figma node 7086:30438 (18×18 in an 18.7 viewBox). */
export function WifiOffGlyph({ size, className }: GlyphProps): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18.7 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M0.70001 5.73887C5.04287 0.753709 12.3572 0.753709 16.7 5.73887M3.55715 10.0119C6.4143 6.87834 10.9857 6.87834 13.8429 10.0119M6.4143 14C7.78572 12.5757 9.6143 12.5757 10.9857 14"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="8.70001" cy="16.2" r="1.2" fill="currentColor" />
      <line
        x1="2.70001"
        y1="14.8686"
        x2="14.2966"
        y2="3.27208"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * Premium star — Figma node 6999:30510 (28×28). Brand-coloured asset
 * (#EA4A27, theme-invariant per Figma), so it carries its own fill
 * rather than `currentColor`.
 */
export function StarGlyph({ size, className }: GlyphProps): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M12.6678 4.09791C13.0878 2.80851 14.912 2.80851 15.3306 4.09791L16.8286 8.70671C16.9202 8.9874 17.0981 9.23195 17.337 9.40546C17.5758 9.57895 17.8635 9.67249 18.1586 9.67271H23.0054C24.362 9.67271 24.9248 11.4087 23.8286 12.2067L19.9086 15.0543C19.6694 15.2279 19.4911 15.4729 19.3996 15.7541C19.308 16.0353 19.3078 16.3382 19.399 16.6195L20.897 21.2283C21.317 22.5177 19.84 23.5915 18.741 22.7935L14.821 19.9459C14.582 19.7723 14.2941 19.6788 13.9986 19.6788C13.7031 19.6788 13.4152 19.7723 13.176 19.9459L9.25608 22.7935C8.15848 23.5915 6.68288 22.5177 7.10148 21.2283L8.59948 16.6195C8.69073 16.3382 8.69056 16.0353 8.59902 15.7541C8.50747 15.4729 8.32924 15.2279 8.08988 15.0543L4.17128 12.2081C3.07508 11.4101 3.63928 9.67411 4.99448 9.67411H9.83988C10.1353 9.67418 10.4233 9.58078 10.6624 9.40727C10.9016 9.23375 11.0797 8.98902 11.1712 8.70811L12.6692 4.09931L12.6678 4.09791Z"
        fill="#EA4A27"
      />
    </svg>
  )
}

/** `magnifyingglass` — error/empty state icon (spec §5.4). */
export function MagnifierGlyph({ size, className }: GlyphProps): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 56 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M24 38C31.732 38 38 31.732 38 24C38 16.268 31.732 10 24 10C16.268 10 10 16.268 10 24C10 31.732 16.268 38 24 38Z"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path d="M34 34L48 48" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

/** `info.circle` — Figma node 9206:60038 (SD/StationDetailsLink, 22×22 export). */
export function InfoGlyph({ size, className }: GlyphProps): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M10.9 20.8984C16.4228 20.8984 20.9 16.4213 20.9 10.8984C20.9 5.37559 16.4228 0.898438 10.9 0.898438C5.37715 0.898438 0.899994 5.37559 0.899994 10.8984C0.899994 16.4213 5.37715 20.8984 10.9 20.8984Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.9 9.89844V15.8984"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.9 5.89844H10.9095"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
